// MACHINERY V2 (Stroje) — a min–max RANGE row sized for touch (M2.16c).
//
// The sheet counterpart of catalog/catalogRailAtoms.tsx `RailRangeRow`, which is
// a 30px desktop control and cannot be reused below 1280 where the ">= 44px both
// axes" law applies. Same shape (eyebrow above, two mono inputs, an en dash
// between), different size.
//
// >>> ITS OWN FILE, NOT AN ADDITION TO sheetAtoms.tsx. <<< That file stands at
// 270 of the project's 300-line cap, so adding a control with two stateful
// inputs to it would force a split of a component set that shipped and was
// reviewed at M2.16a. TouchChip.tsx set the precedent: a sheet/kit control big
// enough to carry its own reasoning gets its own file.
//
// ============================================================
//  THE INPUT KEEPS ITS OWN RAW STRING — THE SAME DEFECT, THE SAME FIX
// ============================================================
// `value={draft.minRpm ?? ""}` makes the field a mirror of the PARSED number, so
// a trailing "." in an HP value is erased under the cursor. catalogRailAtoms.tsx's
// header records this as v1's bug (CatalogBrowsePage.tsx:784). The raw text is
// therefore local and only the PARSED value is published, exactly as the rail
// does.
//
// >>> BUT THERE IS NO DEBOUNCE HERE, DELIBERATELY. <<< The rail debounces at
// 350 ms because each publish writes the live query and the browse hook's load
// effect fires a request per keystroke. This row publishes into a STAGED draft
// (catalog/useCatalogFilterDraft.ts) that issues nothing until the footer's
// primary is pressed, so there is no request to coalesce — and a debounce would
// instead create a window where pressing "apply" immediately after typing
// commits the value BEFORE the last keystroke arrived. Publishing synchronously
// is what makes the primary always commit what is on screen.
//
// ============================================================
//  >>> ...AND THAT PROMISE WAS BROKEN TWICE — review finding F3
// ============================================================
// The box could display a different number from the one it had committed:
//   · THE RE-SEED TEST COMPARED TEXT. `incoming === publishedRef.current`
//     rejected "7" against a published "07", so typing a leading zero rewrote
//     the box under the cursor — where the rail's test is NUMERIC
//     (`Number(published) === value`, catalogRailAtoms.tsx:141-146) and treats
//     "07" as the echo it is. The same text test also wiped the trailing "." the
//     raw string exists to protect, because `String(1) !== "1."`.
//   · THE BOX ACCEPTED CHARACTERS ITS PARSER DROPS. `type="text"` with only an
//     `inputMode` hint let "1.5" into a `parseRangeInt` field. The draft then
//     held 1 while the box still read "1.5" — and the re-seed could not correct
//     it, because it is keyed on `value`, which never changed.
// Both are closed below: the rail's numeric echo test, and a per-field
// numeric-domain guard that refuses what the parser would silently drop.
//
// >>> WHY `type="text"` AND NOT THE RAIL'S `type="number"`. <<< A controlled
// number input sanitises its own value to "" for anything that is not a valid
// floating-point number — including the partial "1." — so adopting it would
// hand this component an empty string mid-keystroke and publish a CLEARED
// bound. `inputMode` still raises the numeric keypad on a phone, and the guard
// below is stricter than the browser's sanitiser anyway: it knows which of the
// two parsers this particular field uses.
//
// RE-SEEDED WHEN THE DRAFT CHANGES FROM OUTSIDE (the sheet's "Zrusit vsetko", or
// a re-open) — tracked against the raw text this input last published, so an
// in-flight edit is never clobbered by its own echo.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React, { useEffect, useRef, useState } from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";

const inputStyle: React.CSSProperties = {
  width: "100%",
  // >= 44px BOTH axes. The width half is `flex: 1` inside the row below, which
  // is far past 44 on any phone; `minWidth: 44` is the floor the law asks for.
  height: 44,
  minWidth: 44,
  padding: "0 11px",
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--card)",
  color: "var(--ink)",
  fontFamily: "var(--font-mono)",
  fontSize: 13,
  outline: "none",
};

/** Digits only. Everything `parseRangeInt` would silently truncate — a decimal
 *  point, a comma, an exponent, a sign — is refused before it reaches the box. */
const INT_TEXT = /^\d*$/;
/** Digits with at most one point, leading or trailing point allowed so "1." and
 *  ".5" can be typed. `parseRangeFloat` reads all three the same way. */
const DECIMAL_TEXT = /^\d*\.?\d*$/;

interface RangeInputProps {
  value?: number;
  onChange: (raw: string) => void;
  placeholder: string;
  ariaLabel: string;
  /** Which parser the caller publishes through — see SheetRangeRowProps. */
  allowDecimal: boolean;
}

const RangeInput: React.FC<RangeInputProps> = ({
  value,
  onChange,
  placeholder,
  ariaLabel,
  allowDecimal,
}) => {
  const [raw, setRaw] = useState<string>(value === undefined ? "" : String(value));
  /** The last text THIS input published — the echo guard described above.
   *  `null` = nothing published yet, so any arriving value is an outside one. */
  const publishedRef = useRef<string | null>(null);

  useEffect(() => {
    const published = publishedRef.current;
    if (published !== null) {
      // NUMERIC, exactly as catalogRailAtoms.tsx:141-146 does it: "07" and "7"
      // are the same bound, and so are "1." and "1". A text comparison here is
      // half of review finding F3.
      const parsed = published.trim() === "" ? undefined : Number(published);
      const isEcho = value === undefined ? published.trim() === "" : parsed === value;
      if (isEcho) return;
    }
    // An OUTSIDE change (Zrusit vsetko, a re-open, a fresh mount) — adopt it.
    publishedRef.current = null;
    setRaw(value === undefined ? "" : String(value));
  }, [value]);

  return (
    <input
      type="text"
      inputMode={allowDecimal ? "decimal" : "numeric"}
      value={raw}
      aria-label={ariaLabel}
      placeholder={placeholder}
      onChange={(e) => {
        const next = e.target.value;
        // REFUSED, not corrected: leaving the box untouched keeps the caret
        // where the user put it, and keeps "what is on screen" equal to "what
        // is committed" at every keystroke rather than after a re-render.
        if (!(allowDecimal ? DECIMAL_TEXT : INT_TEXT).test(next)) return;
        setRaw(next);
        publishedRef.current = next;
        onChange(next);
      }}
      style={inputStyle}
    />
  );
};

export interface SheetRangeRowProps {
  title: string;
  min?: number;
  max?: number;
  /**
   * TRUE when the caller publishes through `parseRangeFloat`, FALSE when it
   * publishes through `parseRangeInt`. Required rather than defaulted: the box
   * must refuse exactly what its own parser drops, and a default would let a new
   * integer field silently accept decimals again (review finding F3).
   */
  allowDecimal: boolean;
  onMin: (raw: string) => void;
  onMax: (raw: string) => void;
}

export const SheetRangeRow: React.FC<SheetRangeRowProps> = ({
  title,
  min,
  max,
  allowDecimal,
  onMin,
  onMax,
}) => {
  const { translate } = useTranslation("myMachinery");
  const minLabel = translate("myMachinery:min");
  const maxLabel = translate("myMachinery:max");

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0 2px", minWidth: 0 }}>
      <RangeInput
        value={min}
        onChange={onMin}
        placeholder={minLabel}
        ariaLabel={`${title} ${minLabel}`}
        allowDecimal={allowDecimal}
      />
      <span style={{ color: "var(--faint)", fontSize: 12, flexShrink: 0 }} aria-hidden>
        –
      </span>
      <RangeInput
        value={max}
        onChange={onMax}
        placeholder={maxLabel}
        ariaLabel={`${title} ${maxLabel}`}
        allowDecimal={allowDecimal}
      />
    </div>
  );
};

export default SheetRangeRow;
